import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { ArrowRight, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INTENTS = {
  rfq: {
    title: "Create Your RFQ™",
    subtitle:
      "Tell us a little about you. We'll set up your Workspace and walk you through your first structured RFQ.",
    cta: "Reserve my Workspace",
  },
  workspace: {
    title: "Create Your Free Workspace",
    subtitle:
      "One Workspace for RFQ™, CommodityBid™, FreightIQ™, Inspection™ and Live Shipment Tracking™.",
    cta: "Create Workspace",
  },
};

export default function SignupDialog({ open, intent = "rfq", onOpenChange }) {
  const meta = INTENTS[intent] || INTENTS.rfq;
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    company: "",
    role: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open) {
      setDone(false);
      setSubmitting(false);
    }
  }, [open]);

  const onChange = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, {
        ...form,
        intent,
        source: "rfq-landing",
      });
      setDone(true);
      toast.success("You're on the list. Check your inbox.", {
        description: "We'll be in touch within one business day.",
      });
    } catch (err) {
      console.error(err);
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="signup-dialog"
        className="sm:max-w-[520px] p-0 overflow-hidden bg-white border-black/[0.08] rounded-2xl"
      >
        <div className="p-6 sm:p-7">
          <DialogHeader>
            <span className="eyebrow text-black/60">Workspace Access</span>
            <DialogTitle className="mt-3 font-display text-[26px] sm:text-[30px] font-bold tracking-tight">
              {meta.title}
            </DialogTitle>
            <DialogDescription className="text-[13.5px] text-black/60 leading-relaxed">
              {meta.subtitle}
            </DialogDescription>
          </DialogHeader>

          {done ? (
            <div className="mt-6 rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-5">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5" />
                <div>
                  <h4 className="font-display text-[16px] font-semibold tracking-tight">
                    You&apos;re in.
                  </h4>
                  <p className="mt-1 text-[13px] text-black/65">
                    We&apos;ll reach out to <strong className="text-black/80">{form.email}</strong>{" "}
                    within one business day with your Workspace access link.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 grid gap-3" data-testid="signup-form">
              <Field
                label="Full name"
                placeholder="Aylin Demir"
                value={form.full_name}
                onChange={onChange("full_name")}
                testId="signup-full-name"
              />
              <Field
                label="Work email"
                type="email"
                required
                placeholder="aylin@yourcompany.com"
                value={form.email}
                onChange={onChange("email")}
                testId="signup-email"
              />
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="Company"
                  placeholder="Acme Imports"
                  value={form.company}
                  onChange={onChange("company")}
                  testId="signup-company"
                />
                <Field
                  label="Role"
                  placeholder="Head of Procurement"
                  value={form.role}
                  onChange={onChange("role")}
                  testId="signup-role"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="signup-submit-btn"
                className="mt-2 btn-primary inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Submitting…" : meta.cta}
                {!submitting && <ArrowRight className="h-4 w-4" />}
              </button>

              <p className="text-[11px] text-black/45 mt-1 leading-relaxed">
                By continuing you agree to receive a single Workspace access email.
                No spam, no marketing lists.
              </p>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, type = "text", placeholder, value, onChange, required, testId }) {
  return (
    <label className="block">
      <span className="font-mono text-[10.5px] tracking-[0.18em] text-black/55 uppercase">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        data-testid={testId}
        className="mt-1.5 w-full rounded-lg border border-black/[0.10] bg-white px-3.5 py-2.5 text-[14px] outline-none focus:border-[#0044FF] focus:ring-2 focus:ring-[#0044FF]/15 transition-colors"
      />
    </label>
  );
}
